/**
-- =============================================================================
-- Service: moderationService.ts
-- Description: Handles reporting and moderation workflows for Phase 10 Step 2.
-- Authoritative Specification: docs/19_v3_feature_specification_draft.md §5
-- =============================================================================
*/

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';
import type {
  CreateReportPayload,
  ModerationActionPayload,
  ModerationFilters,
  ModerationLog,
  ModeratorStatus,
  Report,
} from '../types';

export class ModerationError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'ModerationError';
    this.code = code;
  }
}

/**
 * Submits a new user report for inappropriate content or behaviour.
 */
export async function createReport(
  client: SupabaseClient<Database>,
  payload: CreateReportPayload
): Promise<Report> {
  if (!client) {
    throw new ModerationError('UNAUTHENTICATED', 'Supabase client is required.');
  }

  if (!payload.reporterId || !payload.reporterId.trim()) {
    throw new ModerationError('INVALID_INPUT', 'Reporter ID is required.');
  }

  if (!payload.targetId || !payload.targetId.trim()) {
    throw new ModerationError('INVALID_INPUT', 'Target ID is required.');
  }

  if (!payload.targetType || !['meme', 'comment', 'profile'].includes(payload.targetType)) {
    throw new ModerationError('INVALID_INPUT', 'Valid target type (meme, comment, profile) is required.');
  }

  const validReasons = ['spam', 'harassment', 'hate_speech', 'nsfw', 'copyright', 'other'];
  if (!payload.reason || !validReasons.includes(payload.reason)) {
    throw new ModerationError('INVALID_INPUT', 'A valid report reason is required.');
  }

  const cleanDetails = payload.details ? payload.details.trim().slice(0, 500) : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.from('reports') as any)
    .insert({
      reporter_id: payload.reporterId.trim(),
      target_type: payload.targetType,
      target_id: payload.targetId.trim(),
      reason: payload.reason,
      details: cleanDetails,
      status: 'pending',
    })
    .select()
    .single();

  if (error || !data) {
    throw new ModerationError('REPORT_FAILED', `Failed to submit report: ${error?.message || 'Unknown error'}`);
  }

  return data as Report;
}

/**
 * Checks whether the current user has moderator or admin privileges.
 */
export async function fetchModeratorStatus(
  client: SupabaseClient<Database>,
  userId: string
): Promise<ModeratorStatus> {
  if (!client || !userId || !userId.trim()) {
    return { isModerator: false, isAdmin: false, role: 'user', isSuspended: false };
  }

  try {
    const { data, error } = await client
      .from('profiles')
      .select('role, is_suspended')
      .eq('id', userId.trim())
      .single();

    if (error || !data) {
      return { isModerator: false, isAdmin: false, role: 'user', isSuspended: false };
    }

    const row = data as { role?: string; is_suspended?: boolean };
    const role = (row.role === 'admin' || row.role === 'moderator') ? row.role : 'user';
    const isSuspended = Boolean(row.is_suspended);

    return {
      isModerator: role === 'moderator' || role === 'admin',
      isAdmin: role === 'admin',
      role,
      isSuspended,
    };
  } catch {
    return { isModerator: false, isAdmin: false, role: 'user', isSuspended: false };
  }
}

/**
 * Lists reports for the moderation dashboard with optional filters.
 */
export async function listReports(
  client: SupabaseClient<Database>,
  filters?: ModerationFilters,
  limit: number = 50
): Promise<Report[]> {
  if (!client) {
    throw new ModerationError('UNAUTHENTICATED', 'Supabase client is required.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (client.from('reports') as any)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (filters?.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters?.targetType && filters.targetType !== 'all') {
    query = query.eq('target_type', filters.targetType);
  }

  const { data, error } = await query;

  if (error) {
    throw new ModerationError('QUERY_FAILED', `Failed to load reports: ${error.message}`);
  }

  return (data || []) as Report[];
}

/**
 * Executes a moderation action (dismiss, hide_target, suspend_user) on a report.
 */
export async function executeModerationAction(
  client: SupabaseClient<Database>,
  payload: ModerationActionPayload
): Promise<{ success: boolean; reportId: string; action: string }> {
  if (!client) {
    throw new ModerationError('UNAUTHENTICATED', 'Supabase client is required.');
  }

  if (!payload.reportId) {
    throw new ModerationError('INVALID_INPUT', 'Report ID is required.');
  }

  // 1. First attempt atomic RPC function
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: rpcData, error: rpcError } = await (client as any).rpc('moderate_content', {
      p_report_id: payload.reportId,
      p_action: payload.action,
      p_reason: payload.reason || '',
    });

    if (!rpcError && rpcData) {
      return {
        success: true,
        reportId: payload.reportId,
        action: payload.action,
      };
    }
  } catch {
    // Fall back to direct PostgREST operations if RPC is unavailable
  }

  // 2. Direct PostgREST fallback
  const nextStatus = payload.action === 'dismiss' ? 'dismissed' : 'actioned';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: updateError } = await (client.from('reports') as any)
    .update({
      status: nextStatus,
      action_taken: payload.action,
      updated_at: new Date().toISOString(),
    })
    .eq('id', payload.reportId);

  if (updateError) {
    throw new ModerationError('ACTION_FAILED', `Failed to apply moderation action: ${updateError.message}`);
  }

  return {
    success: true,
    reportId: payload.reportId,
    action: payload.action,
  };
}

/**
 * Fetches recent moderation audit logs.
 */
export async function fetchModerationLogs(
  client: SupabaseClient<Database>,
  limit: number = 30
): Promise<ModerationLog[]> {
  if (!client) {
    throw new ModerationError('UNAUTHENTICATED', 'Supabase client is required.');
  }

  const { data, error } = await client
    .from('moderation_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new ModerationError('QUERY_FAILED', `Failed to load moderation logs: ${error.message}`);
  }

  return (data || []) as ModerationLog[];
}
